
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import Loading from '@/common/components/atom/loading/loading';
import Skeleton from '@/common/components/atom/skeleton/skeleton';
import Text from '@/common/components/atom/text/text';
import Transition from '@/common/components/atom/transition/transition';
import { LayoutWithHeaderAndFooter } from '@/common/components/layouts/layoutWithHeaderAndFooter';
import Seo from '@/common/components/layouts/seo';
import { withCSR } from '@/common/hoc/withCsr';
import { withServerUtils } from '@/common/hoc/withServerUtils';
import { CENTERS } from '@/common/types/centers';
import { picUserImageUrl } from '@/common/utils/picUserImageUrl';
import { useBookingProfileData } from '@/modules/booking/hooks/useBookingProfileData';
import BookingSteps from '@/modules/booking/views';
import { BOOKING_PAGE_FOOTER_ID } from '@/modules/samanBooking/localPrivateSpecialists/BookingPageFooter';
import { getLocalPrivateSpecialistsConfig } from '@/modules/samanBooking/localPrivateSpecialists/config';
import { buildLocalSpecialistsEvent } from '@/modules/samanBooking/localPrivateSpecialists/events';
import { splunkInstance } from '@/common/services/splunk';
import Link from 'next/link';
import DoctorInfo from '@/modules/myTurn/components/doctorInfo';
import { useProfileDataStore } from '@/modules/profile/store/profileData';
import { useAbsentScore } from '@/common/apis/services/ravi/absentScore';
import moment from 'jalali-moment';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext } from 'next/types';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { samanDoctorAvailabilityStatus } from '@/common/apis/services/booking/samanDoctorAvailabilityStatus';

const Booking = () => {
  const router = useRouter();
  const setProfileData = useProfileDataStore(state => state.setData);

  const slug = router.query?.slug?.toString();
  const {
    data,
    profile: profileData,
    isLoading: bookingProfileLoading,
    isSuccess,
    status,
  } = useBookingProfileData(slug, {
    enabled: !!router.isReady && !!slug,
  });

  const doctorAvatarFromPic = useMemo(() => {
    const userId = profileData?.user_id;
    if (userId == null) return undefined;
    return picUserImageUrl(userId);
  }, [profileData?.user_id]);

  const { data: absentScoreData } = useAbsentScore(slug, !!router.isReady);

  const doctorName = useMemo(() => {
    const fromProfile = `${profileData?.name ?? ''} ${profileData?.family ?? ''}`.trim();
    if (fromProfile) return fromProfile;
    return profileData?.display_name ?? '';
  }, [profileData?.name, profileData?.family, profileData?.display_name]);

  useEffect(() => {
    if (data?.redirect) {
      router.replace(data.redirect?.route?.replace('/dr', '/booking') + location.search);
    }
  }, [data?.redirect]);

  useEffect(() => {
    if (!router.query?.centerId) return;
    if (profileData && !profileData?.centers?.some((item: any) => item.id === router.query?.centerId)) {
      router.replace(`/booking/${router.query?.slug?.toString()}`);
    }
  }, [profileData]);

  const isLoading = bookingProfileLoading;

  const queryHandler = useCallback((queries: any) => {
    const payloads = Object.keys(queries);
    if (
      payloads.includes('centerId') &&
      payloads.includes('serviceId') &&
      (payloads.includes('timeId') ||
        payloads.includes('time') ||
        payloads.includes('reserveId') ||
        payloads.includes('skipTimeSelectStep'))
    ) {
      return {
        step: 'SELECT_USER',
        payload: queries as any,
      };
    }
    if (payloads.includes('centerId') && payloads.includes('serviceId') && payloads.includes('bookRequest'))
      return {
        step: 'SELECT_USER',
        payload: queries as any,
      };
    if (payloads.includes('centerId') && payloads.includes('serviceId')) {
      const serviceId = queries.serviceId?.toString();
      if (!serviceId || serviceId === '-1') {
        return {
          step: 'SELECT_SERVICES',
          payload: queries as any,
        };
      }
      return {
        step: 'SELECT_TIME',
        payload: queries as any,
      };
    }
    if (payloads.includes('centerId')) {
      return {
        step: 'SELECT_SERVICES',
        payload: queries as any,
      };
    }
    return {
      step: 'SELECT_CENTER',
      payload: queries as any,
    };
  }, []);

  useEffect(() => {
    if (isSuccess && profileData) {
      setProfileData(profileData);
    }
  }, [isSuccess, profileData, status]);

  useEffect(() => {
    // Prefetch the factor page
    router.prefetch('/factor/[centerId]/[bookId]');
    // Prefetch the receipt page
    router.prefetch('/receipt/[centerId]/[bookId]');
  }, []);

  const centerName = useMemo(() => {
    const center = profileData?.centers?.find((center: any) => center.id === router.query.centerId);
    return center?.center_type === 1 ? `مطب ${doctorName}` : center?.name;
  }, [router.query.centerId, profileData]);

  const penaltyScoreItem = absentScoreData?.list?.find(item => item.penalty_score != null && item.penalty_score !== undefined);
  const penaltyScore = penaltyScoreItem?.penalty_score;
  
  const getAlertConfig = () => {
    if (!penaltyScore || penaltyScore === 0) {
      return null;
    }
    
    if (penaltyScore >= 1) {
      return {
        bgColor: 'bg-red-50',
        borderColor: 'border-red-300',
        textColor: 'text-red-600',
        message: 'طبق نظر مراجعین، "حتما" پیش از مراجعه، از حضور درمانگر در مرکز اطمینان حاصل کنید.',
      };
    }
    
    if (penaltyScore > 0.1 && penaltyScore < 1) {
      return {
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300',
        textColor: 'text-black',
        message: 'طبق نظر مراجعین، حتما پیش از مراجعه، از حضور درمانگر در مرکز اطمینان حاصل کنید.',
      };
    }
    
    if (penaltyScore <= 0.1) {
      return {
        bgColor: 'bg-[#F2F2F2]',
        borderColor: 'border-gray-300',
        textColor: 'text-black',
        message: 'طبق نظر مراجعین، پیش از مراجعه، از حضور درمانگر در مرکز اطمینان حاصل کنید.',
      };
    }
    
    return null;
  };
  
  const alertConfig = getAlertConfig();

  return (
    <>
      <Seo title={`دریافت نوبت ${doctorName ? `از ${doctorName}` : ''}`} noIndex />
      {alertConfig && (
        <div className="w-full max-w-screen-lg mx-auto px-4 pt-4 md:px-0">
          <div className={`${alertConfig.bgColor} ${alertConfig.borderColor} border rounded-lg p-3`}>
            <Text fontSize="sm" className={alertConfig.textColor}>
              {alertConfig.message}
            </Text>
          </div>
        </div>
      )}
      <div className="flex flex-col-reverse items-start w-full max-w-screen-lg mx-auto md:flex-row space-s-0 md:space-s-5 md:py-10">
        <div className="flex flex-col w-full bg-white md:basis-4/6 md:rounded-lg shadow-card mb-28">
          {isLoading && (
            <div className="self-center p-10">
              <Loading className="self-center" />
            </div>
          )}
          <Transition match={!!queryHandler(router.query) && !isLoading} animation="bottom">
            {<BookingSteps defaultStep={queryHandler(router.query) as any} slug={router.query?.slug?.toString() ?? '/'} />}
          </Transition>
        </div>
        <div className="w-full p-3 mb-2 space-y-3 bg-white md:rounded-lg shadow-card md:mb-0 md:basis-2/6 ">
          {/* config.inlineOnBookingError (Dr. Taheri): the doctor box opens the profile; looks exactly the same. */}
          {slug && getLocalPrivateSpecialistsConfig(slug)?.inlineOnBookingError ? (
            <Link
              href={`/dr/${slug}/`}
              className="block no-underline text-inherit"
              data-testid="booking-doctor-profile-link"
              onClick={() =>
                splunkInstance('search').sendEvent(
                  buildLocalSpecialistsEvent(
                    'click-own-profile',
                    { slug, placement: 'booking-header', config: getLocalPrivateSpecialistsConfig(slug)! },
                    { target: `/dr/${slug}/`, ...(router.query.centerId && { center_id: router.query.centerId.toString() }) },
                  ),
                )
              }
            >
              <DoctorInfo className="p-4 rounded-lg bg-slate-100" slug={slug} avatar={doctorAvatarFromPic} />
            </Link>
          ) : (
            <DoctorInfo
              className="p-4 rounded-lg bg-slate-100"
              slug={router.query?.slug?.toString()}
              avatar={doctorAvatarFromPic}
            />
          )}

          {router.query.centerId && (
            <div className="flex flex-col px-2 py-1 space-y-1 border-r-2 border-slate-200">
              <Text fontSize="xs" className="opacity-70">
                {router.query.centerId === CENTERS.CONSULT ? 'خدمت' : 'مرکز'}
              </Text>
              {isLoading && <Skeleton w="9rem" h="0.8rem" className="!mt-2 !mb-1" rounded="full" />}
              {isSuccess && (
                <Text fontSize="sm" fontWeight="medium">
                  {router.query.centerId === CENTERS.CONSULT
                    ? `ویزیت آنلاین ${profileData?.online_visit_channel_types?.length ? 'در پیام رسان' : ''}`
                    : centerName}
                </Text>
              )}
            </div>
          )}

          {router.query?.time && (
            <div className="flex flex-col px-2 py-1 space-y-1 border-r-2 border-slate-200">
              <Text fontSize="xs" className="opacity-70">
                زمان نوبت
              </Text>
              {isLoading && <Skeleton w="9rem" h="0.8rem" className="!mt-2 !mb-1" rounded="full" />}
              {isSuccess && (
                <Text fontSize="sm" fontWeight="medium">
                  {moment(router.query.time)?.locale('fa')?.calendar?.(undefined, {
                    sameDay: 'امروز (dddd) - ساعت: HH:mm',
                    nextDay: 'فردا (dddd) - ساعت: HH:mm',
                    sameElse: 'dddd jD jMMMM - ساعت: HH:mm',
                  })}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Empty for everyone; BookingPageFooter portals the clinic support line here, under the booking card. */}
      <div id={BOOKING_PAGE_FOOTER_ID} className="w-full max-w-screen-lg mx-auto" />
    </>
  );
};

Booking.getLayout = function getLayout(page: ReactElement) {
  return (
    <LayoutWithHeaderAndFooter shouldShowPromoteApp={false} showBottomNavigation={false} {...page.props.config} showFooter={false}>
      {page}
    </LayoutWithHeaderAndFooter>
  );
};

 const _getServerSideProps = withCSR(
  withServerUtils(async (context: GetServerSidePropsContext) => {
    const { id, center_id, slug: rawSlug } = context.query;
    if (id && center_id) {
      return {
        redirect: {
          statusCode: 302,
          destination: `/receipt/${center_id}/${id}`,
        },
      };
    }

    const queryClient = new QueryClient();
    const slug = typeof rawSlug === 'string' ? decodeURIComponent(rawSlug) : undefined;

    if (slug) {
      await queryClient
        .fetchQuery([ServerStateKeysEnum.SamanDoctorAvailabilityStatus, slug], () => samanDoctorAvailabilityStatus(slug))
        .catch(error => {
          console.error('Error prefetching Saman availability status on booking page:', error);
        });
    }

    return {
      props: {
        query: context.query,
        dehydratedState: dehydrate(queryClient),
      },
    };
  }),
);

export default Booking;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/booking/[slug]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  